import 'twin.macro';

import React, { Fragment, Suspense } from 'react';
import { useEffect } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { BeatLoader } from 'react-spinners';

import { useGetCountriesMainPageQuery } from '@/app/api/apply';
import {
  deposit_physical_allowed,
  deposit_virtual_allowed,
  invest_physical_allowed,
  invest_virtual_allowed,
  is_deposit_card_open,
  is_invest_card_open,
  is_prepaid_card_open
} from '@/app/availlability_vars';
import { nr_pathnames } from '@/app/utils/constants';
import { RequireAuth } from '@/helper/RequireAuth';

import StepLayout from '../views/layouts/step-layout';
import PrepaidApplyPage from './prepaid-card-steps/apply-page';
import PrepaidCardTypePage from './prepaid-card-steps/card-type-page';
import PrepaidSuccessPage from './prepaid-card-steps/success-page';
import PrepaidVerifyPage from './prepaid-card-steps/verify-page';
import PrepaidWaitingPage from './prepaid-card-steps/waiting-page';
import PendingPage from './steps/pending-page';

const ApplyPage = React.lazy(() => import('@/pages/steps/apply-page'));
const VerifyPage = React.lazy(() => import('@/pages/steps/verify-page'));
const BiometryPage = React.lazy(() => import('@/pages/steps/biometry-page'));
const InternalChecksPage = React.lazy(() => import('@/pages/steps/internal-checks-page'));
const AddressPage = React.lazy(() => import('./steps/address-page'));
const DocumentsPage = React.lazy(() => import('@/pages/steps/documents-page'));
const VideoCallPage = React.lazy(() => import('@/pages/steps/videocall-page'));
const SuccessPage = React.lazy(() => import('@/pages/steps/success-page'));
const DigitalSuccessPage = React.lazy(() => import('@/pages/steps/digital-success-page'));

const NrSteps = () => {
  const isAuth = true;
  const location = useLocation().pathname;
  const isDeposit = location.includes('deposit');

  const physical_card_allowed = !isDeposit ? invest_physical_allowed : deposit_physical_allowed;
  const virtual_card_allowed = !isDeposit ? invest_virtual_allowed : deposit_virtual_allowed;

  const { data: countriesData, isLoading } = useGetCountriesMainPageQuery(
    location.includes('prepaid') ? { is_prepaid_available: true } : { is_visible: true }
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);
  if (!isAuth) return <Navigate to={nr_pathnames.landing} replace={true} />;

  if (isLoading)
    return (
      <div tw='w-full flex justify-center h-[95vh] items-center'>
        <BeatLoader color='rgb(42, 134, 64)' />
      </div>
    );

  return (
    <Fragment>
      <Suspense
        fallback={
          <div tw='w-full flex justify-center h-[95vh] items-center'>
            <BeatLoader color='rgb(42, 134, 64)' />
          </div>
        }
      >
        <Routes>
          {is_deposit_card_open && (
            <Route path={nr_pathnames.deposit_card} element={<StepLayout />}>
              <Route path={nr_pathnames.apply} element={<ApplyPage countries={countriesData} />} />

              <Route path={nr_pathnames.verify} element={<VerifyPage />} />

              <Route
                path={nr_pathnames.biometry}
                element={
                  <RequireAuth>
                    <BiometryPage />
                  </RequireAuth>
                }
              />
              <Route
                path={nr_pathnames.internal_checks}
                element={
                  <RequireAuth>
                    <InternalChecksPage />
                  </RequireAuth>
                }
              />
              <Route
                path={nr_pathnames.delivery}
                element={
                  <RequireAuth>
                    {/* <DeliveryPage countries={countriesData} /> */}
                    <AddressPage
                      countriesData={countriesData}
                      physical_card_allowed={physical_card_allowed}
                      virtual_card_allowed={virtual_card_allowed}
                    />
                  </RequireAuth>
                }
              />
              <Route
                path={nr_pathnames.documents}
                element={
                  <RequireAuth>
                    <DocumentsPage />
                  </RequireAuth>
                }
              />
              <Route
                path={nr_pathnames.videocall}
                element={
                  <RequireAuth>
                    <VideoCallPage />
                  </RequireAuth>
                }
              />
              <Route
                path={nr_pathnames.done}
                element={
                  <RequireAuth>
                    <SuccessPage />
                  </RequireAuth>
                }
              />
              <Route
                path={nr_pathnames.digital_done}
                element={
                  <RequireAuth>
                    <DigitalSuccessPage />
                  </RequireAuth>
                }
              />
              <Route
                path={nr_pathnames.pending}
                element={
                  <RequireAuth>
                    <PendingPage />
                  </RequireAuth>
                }
              />
            </Route>
          )}

          {is_invest_card_open && (
            <Route path={nr_pathnames.invest_card} element={<StepLayout />}>
              <Route path={nr_pathnames.apply} element={<ApplyPage countries={countriesData} />} />

              <Route path={nr_pathnames.verify} element={<VerifyPage />} />

              <Route
                path={nr_pathnames.biometry}
                element={
                  <RequireAuth>
                    <BiometryPage />
                  </RequireAuth>
                }
              />
              <Route
                path={nr_pathnames.internal_checks}
                element={
                  <RequireAuth>
                    <InternalChecksPage />
                  </RequireAuth>
                }
              />
              <Route
                path={nr_pathnames.delivery}
                element={
                  <RequireAuth>
                    {/* <DeliveryPage countries={countriesData} /> */}
                    <AddressPage
                      countriesData={countriesData}
                      physical_card_allowed={physical_card_allowed}
                      virtual_card_allowed={virtual_card_allowed}
                    />
                  </RequireAuth>
                }
              />
              <Route
                path={nr_pathnames.documents}
                element={
                  <RequireAuth>
                    <DocumentsPage />
                  </RequireAuth>
                }
              />
              <Route
                path={nr_pathnames.videocall}
                element={
                  <RequireAuth>
                    <VideoCallPage />
                  </RequireAuth>
                }
              />
              <Route path={nr_pathnames.done} element={<SuccessPage />} />
              <Route
                path={nr_pathnames.digital_done}
                element={
                  <RequireAuth>
                    <DigitalSuccessPage />
                  </RequireAuth>
                }
              />
            </Route>
          )}
          {
            <Route path={nr_pathnames.prepaid_card} element={<StepLayout withoutStepper={true} withFooter={true} />}>
              <Route path={nr_pathnames.apply} element={<PrepaidApplyPage countries={countriesData} />} />
              <Route path={nr_pathnames.verify} element={<PrepaidVerifyPage />} />
              <Route
                path={nr_pathnames.pp_card_type}
                element={
                  <RequireAuth>
                    <PrepaidCardTypePage />
                  </RequireAuth>
                }
              />
              <Route
                path={nr_pathnames.pp_waiting}
                element={
                  <RequireAuth>
                    <PrepaidWaitingPage />
                  </RequireAuth>
                }
              />
              <Route path={nr_pathnames.pp_success} element={<PrepaidSuccessPage />} />
            </Route>
          }
        </Routes>
      </Suspense>
    </Fragment>
  );
};

export { NrSteps };
